import React, { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Grid,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { voucherService } from '../services/api';
import { VoucherCreateRequest } from '../types/voucher';

const VoucherCreate: React.FC = () => {
  const [formData, setFormData] = useState<VoucherCreateRequest>({
    merchantId: '',
    merchantOrderReference: '',
    productDescription: '',
    amount: 0,
    currency: 'USD',
  });

  const [createdVoucher, setCreatedVoucher] = useState<any>(null);

  const createMutation = useMutation(
    (data: VoucherCreateRequest) => voucherService.createVoucher(data),
    {
      onSuccess: (data) => {
        setCreatedVoucher(data);
        setFormData({
          merchantId: '',
          merchantOrderReference: '',
          productDescription: '',
          amount: 0,
          currency: 'USD',
        });
      },
    }
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'amount' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Create Voucher
      </Typography>

      {createdVoucher && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Voucher created successfully! Voucher Number: <strong>{createdVoucher.voucherNumber}</strong>
        </Alert>
      )}

      {createMutation.isError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Failed to create voucher. Please try again.
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Merchant ID"
                name="merchantId"
                value={formData.merchantId}
                onChange={handleChange}
                disabled={createMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Order Reference"
                name="merchantOrderReference"
                value={formData.merchantOrderReference}
                onChange={handleChange}
                disabled={createMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                rows={3}
                label="Product Description"
                name="productDescription"
                value={formData.productDescription}
                onChange={handleChange}
                disabled={createMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                inputProps={{ step: '0.01', min: '0.01' }}
                disabled={createMutation.isLoading}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                select
                label="Currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                disabled={createMutation.isLoading}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWG">ZWG</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={createMutation.isLoading}
                startIcon={createMutation.isLoading && <CircularProgress size={20} />}
              >
                {createMutation.isLoading ? 'Creating...' : 'Create Voucher'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default VoucherCreate;